import { LeadOperationsSettings } from '@otw/models';

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

export const getTempTagRecipientEmails = (
  settings: LeadOperationsSettings | null | undefined,
): string[] => {
  return normalizeTempTagRecipientEmails(settings?.temp_tag_recipient_emails ?? []);
};

export const formatTempTagRecipientsForMail = (emails: string[]): string =>
  emails.join(', ');

export interface TempTagMailFields {
  /** Pass to Firestore `mail` as `to: string[]`. Do not join with commas. */
  to: string[];
  cc?: string;
  /** Replies go to the person who sent the request. */
  replyTo?: string;
}

const splitAddressList = (value: string): string[] =>
  value.split(/[,;]+/).map((part) => part.trim()).filter(Boolean);

/**
 * Build the Firestore `mail` recipient fields for a temp tag request.
 * Use `to` as a string array on one mail document. A comma-joined `to` string
 * is stored as one recipient, so later external addresses are dropped.
 */
export const buildTempTagMailFields = (
  toField: string,
  ccField: string,
): TempTagMailFields => {
  const to = normalizeTempTagRecipientEmails(splitAddressList(toField));
  const cc = ccField.trim();
  const replyTo = cc || undefined;

  if (!cc) return { to, replyTo };

  const alreadyIncluded = to.some((email) => email.toLowerCase() === cc.toLowerCase());
  if (alreadyIncluded) return { to, replyTo };

  return { to, cc, replyTo };
};

export const normalizeTempTagRecipientEmails = (emails: string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const email of emails) {
    const trimmed = email.trim();
    if (!trimmed) continue;

    const key = normalizeEmail(trimmed);
    if (seen.has(key)) continue;

    seen.add(key);
    result.push(trimmed);
  }

  return result;
};
